// src/features/compliance/api/useSecurityScans.ts
// Security scanning hooks — security-scan-service endpoints
// OpenAPI source of truth:
//   GET  /security-scan/api/security-scan/scans          → list scans
//   GET  /security-scan/api/security-scan/scans/{id}     → get scan
//   GET  /security-scan/api/security-scan/findings        → list findings
//   GET  /security-scan/api/security-scan/findings/{id}   → get finding
//   PUT  /security-scan/api/security-scan/findings/{id}   → update finding
//   GET  /security-scan/api/security-scan/stats           → scan stats
//   POST /security-scan/api/security-scan/submit/file     → submit file scan
//   POST /security-scan/api/security-scan/submit/git      → submit git scan
// © 2025–2026 MCX Services, LLC. All rights reserved.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

// ============================================================================
// Types — from openapi.json security_scan_service schemas
// ============================================================================

export interface SubmitFileScanRequest {
    project_id: number
    file_upload_id: number
    language: string
    scan_types?: Array<'cve' | 'complexity' | 'fragility'>
}

export interface GitCredentials {
    auth_type: 'token' | 'ssh_key' | 'username_password' | 'oauth_token'
    token?: string | null
    private_key?: string | null
    passphrase?: string | null
    username?: string | null
    password?: string | null
    access_token?: string | null
    refresh_token?: string | null
}

export interface GitConfig {
    repository_url: string
    branch?: string
    credentials: GitCredentials
    shallow_clone?: boolean
    include_submodules?: boolean
    subdirectory?: string | null
}

export interface SubmitGitScanRequest {
    project_id: number
    language: string
    git_config: GitConfig
}

export interface SecurityScan {
    id: string
    project_id: number
    status: string
    scan_types: string[]
    language: string
    total_findings: number
    critical_count: number
    high_count: number
    medium_count: number
    low_count: number
    created_at: string
    completed_at?: string | null
}

export interface SecurityFinding {
    id: number
    scan_id: string
    finding_type: string
    severity: string
    status: string
    title: string
    description: string
    file_path?: string | null
    line_number?: number | null
    cwe_id?: string | null
    cve_id?: string | null
    remediation?: string | null
    assignee_id?: number | null
    notes?: string | null
    created_at: string
    updated_at: string
}

export interface UpdateFindingRequest {
    status?: 'open' | 'acknowledged' | 'remediated' | 'false_positive' | 'wont_fix' | null
    assignee_id?: number | null
    notes?: string | null
}

export interface ScanStats {
    total_scans: number
    total_findings: number
    critical_count: number
    high_count: number
    medium_count: number
    low_count: number
    scans_by_status: Record<string, number>
}

export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low'
export type FindingStatus = 'open' | 'acknowledged' | 'remediated' | 'false_positive' | 'wont_fix'

/** Statuses that indicate a scan is still in flight and warrants polling. */
const ACTIVE_STATUSES = new Set(['pending', 'queued', 'running', 'in_progress', 'processing'])

/** Poll interval while at least one scan is active (ms). */
const POLL_INTERVAL_MS = 5_000

// ============================================================================
// Query Keys
// ============================================================================

export const securityScanKeys = {
    all: ['security-scans'] as const,
    scans: (projectId?: number) => [...securityScanKeys.all, 'scans', projectId] as const,
    scan: (scanId: string) => [...securityScanKeys.all, 'scan', scanId] as const,
    findings: (params?: Record<string, unknown>) => [...securityScanKeys.all, 'findings', params] as const,
    finding: (findingId: number) => [...securityScanKeys.all, 'finding', findingId] as const,
    stats: (projectId?: number) => [...securityScanKeys.all, 'stats', projectId] as const,
}

// ============================================================================
// Scan Queries
// ============================================================================

export const useSecurityScans = (projectId?: number, status?: string) => {
    return useQuery({
        queryKey: securityScanKeys.scans(projectId),
        queryFn: async () => {
            const params: Record<string, string | number> = { limit: 100, offset: 0 }
            if (projectId) params.project_id = projectId
            if (status) params.status = status

            const { data } = await apiClient.get('/api/security-scan/scans', { params })
            return data as { scans: SecurityScan[]; total: number }
        },
        // Poll while any scan is in a non-terminal state; stop automatically when all settle.
        refetchInterval: (query) => {
            const scans = query.state.data?.scans
            if (!scans?.length) return false
            return scans.some((s) => ACTIVE_STATUSES.has(s.status)) ? POLL_INTERVAL_MS : false
        },
    })
}

export const useSecurityScan = (scanId: string) => {
    return useQuery({
        queryKey: securityScanKeys.scan(scanId),
        queryFn: async () => {
            const { data } = await apiClient.get(`/api/security-scan/scans/${scanId}`)
            return data as SecurityScan & { findings: SecurityFinding[] }
        },
        enabled: !!scanId,
        // Poll every 4s while the scan is in a non-terminal state; stops automatically.
        refetchInterval: (query) => {
            const status = query.state.data?.status
            return status && ACTIVE_STATUSES.has(status) ? 4_000 : false
        },
    })
}

// ============================================================================
// Findings Queries
// ============================================================================

export const useSecurityFindings = (params?: {
    project_id?: number
    scan_id?: string
    severity?: string
    finding_type?: string
    status?: string
    limit?: number
    offset?: number
}) => {
    return useQuery({
        queryKey: securityScanKeys.findings(params as Record<string, unknown>),
        queryFn: async () => {
            const queryParams: Record<string, string | number> = {
                limit: params?.limit ?? 100,
                offset: params?.offset ?? 0,
            }
            if (params?.project_id) queryParams.project_id = params.project_id
            if (params?.scan_id) queryParams.scan_id = params.scan_id
            if (params?.severity) queryParams.severity = params.severity
            if (params?.finding_type) queryParams.finding_type = params.finding_type
            if (params?.status) queryParams.status = params.status

            const { data } = await apiClient.get('/api/security-scan/findings', { params: queryParams })
            return data as { findings: SecurityFinding[]; total: number }
        },
    })
}

// ============================================================================
// Stats Query
// ============================================================================

export const useScanStats = (projectId?: number) => {
    return useQuery({
        queryKey: securityScanKeys.stats(projectId),
        queryFn: async () => {
            const params: Record<string, number> = {}
            if (projectId) params.project_id = projectId

            const { data } = await apiClient.get('/api/security-scan/stats', { params })
            return data as ScanStats
        },
    })
}

// ============================================================================
// Mutations
// ============================================================================

export const useSubmitFileScan = () => {
    const qc = useQueryClient()

    return useMutation({
        mutationFn: async (request: SubmitFileScanRequest) => {
            const { data } = await apiClient.post('/api/security-scan/submit/file', request)
            return data
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: securityScanKeys.all })
        },
    })
}

export const useSubmitGitScan = () => {
    const qc = useQueryClient()

    return useMutation({
        mutationFn: async (request: SubmitGitScanRequest) => {
            const { data } = await apiClient.post('/api/security-scan/submit/git', request)
            return data
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: securityScanKeys.all })
        },
    })
}

export const useUpdateFinding = () => {
    const qc = useQueryClient()

    return useMutation({
        mutationFn: async ({ findingId, update }: { findingId: number; update: UpdateFindingRequest }) => {
            const { data } = await apiClient.put(`/api/security-scan/findings/${findingId}`, update)
            return data
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: securityScanKeys.all })
        },
    })
}